import React, { useState } from 'react';
import { Lock, Zap, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

/**
 * UpgradeModal — shown whenever a free user tries to access premium content.
 *
 * Props:
 *   onClose  — called when user dismisses the modal
 *   plan     — plan to upgrade to: "pro" (default) | "enterprise"
 *   billing  — billing cycle to pre-select: "monthly" (default) | "yearly"
 *   feature  — optional context-specific variant; "save" shows the Pro-unlock
 *              checklist used by the shared-session save/duplicate funnel.
 */
const UpgradeModal = ({ onClose, plan = 'pro', billing = 'monthly', feature = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBilling, setSelectedBilling] = useState(billing);

  const handleUpgrade = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      // Not logged in — send to pricing page; user can sign up there
      window.location.href = '/pricing';
      return;
    }
    if (!API_URL) {
      window.location.href = '/pricing';
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/payments/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, billing: selectedBilling }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to start checkout.');
      }
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500" />

        <div className="p-8">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <X className="w-3.5 h-3.5 text-gray-500" />
          </button>

          {/* Icon */}
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-amber-500" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 text-center mb-1.5">
            {feature === 'save'
              ? 'Save your design with Pro'
              : (plan === 'enterprise' ? 'Enterprise Feature' : 'Pro Feature')}
          </h2>
          {feature === 'save' ? (
            <div className="mb-6">
              <p className="text-gray-500 text-sm text-center mb-3">
                Pro unlocks:
              </p>
              <ul className="space-y-1.5 text-sm text-gray-700 mx-auto max-w-xs">
                {[
                  'Save Designs',
                  'My Designs',
                  'Unlimited Downloads',
                  'Reopen Later',
                  'Duplicate Designs',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center mb-6">
              Unlock all premium templates, elements, and export formats by upgrading your plan.
            </p>
          )}

          {/* Billing toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-5">
            <button
              onClick={() => setSelectedBilling('monthly')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                selectedBilling === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setSelectedBilling('yearly')}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                selectedBilling === 'yearly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Yearly
              <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                Save 33%
              </span>
            </button>
          </div>

          {/* Pricing display */}
          <div className="text-center mb-5">
            {plan === 'pro' ? (
              <>
                <span className="text-4xl font-bold text-gray-900">
                  {selectedBilling === 'yearly' ? '$24' : '$2.99'}
                </span>
                <span className="text-gray-400 text-sm ml-1">
                  {selectedBilling === 'yearly' ? '/year' : '/month'}
                </span>
                <p className="text-xs text-orange-600 font-medium mt-0.5">
                  🔥 Founding member price — locked forever
                </p>
              </>
            ) : (
              <>
                <span className="text-4xl font-bold text-gray-900">$7.99</span>
                <span className="text-gray-400 text-sm ml-1">/user/month</span>
                <p className="text-xs text-gray-500 font-medium mt-0.5">minimum 3 seats</p>
              </>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-500 text-xs text-center mb-4 bg-red-50 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          {/* CTA */}
          <button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full h-12 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 disabled:opacity-60 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-md shadow-orange-200 mb-3"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting to checkout…</>
            ) : (
              <><Zap className="w-4 h-4" /> Upgrade to {plan === 'enterprise' ? 'Enterprise' : 'Pro'}</>
            )}
          </button>

          <button
            onClick={onClose}
            className="w-full h-10 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
